/**
 * What one of the caller's bots watches for them: their own watches and reminders on it,
 * and the workflows their admins set up that tell it about them. Read only while open.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiRequest } from '../../../lib/apiClient.js';
import { queryKeys } from '../../../lib/queryKeys.js';

const watchScheduleSchema = z.union([
  z.object({ days: z.array(z.number()), time: z.string() }),
  z.object({ monthDays: z.array(z.number()), time: z.string() }),
  z.object({ on: z.string(), time: z.string() }),
  z.object({ everyMinutes: z.number(), from: z.string() }),
]);

const panelWatchSchema = z.object({
  watchId: z.string(),
  kind: z.enum(['reminder', 'watch']),
  what: z.string(),
  schedule: watchScheduleSchema.nullable(),
  event: z.object({ type: z.string(), title: z.string() }).nullable(),
  endsAt: z.string().nullable(),
  status: z.enum(['scheduled', 'paused', 'broken']),
  brokenReason: z.string().nullable(),
});
export type PanelWatch = z.infer<typeof panelWatchSchema>;

const dutySchema = z.object({
  workflowId: z.string(),
  name: z.string(),
  because: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('group'), groupName: z.string() }),
    z.object({ kind: z.literal('named_in_event') }),
  ]),
});
export type Duty = z.infer<typeof dutySchema>;

const whatIWatchSchema = z.object({
  yours: z.array(panelWatchSchema),
  partOfYourJob: z.array(dutySchema),
});
type WhatIWatch = z.infer<typeof whatIWatchSchema>;

const watchBudgetSchema = z.object({ percentUsed: z.number() });
type WatchBudget = z.infer<typeof watchBudgetSchema>;

export const useWhatIWatchLogic = (botId: string, open: boolean) => {
  const queryClient = useQueryClient();
  const panel = useQuery({
    queryKey: queryKeys.whatIWatch(botId),
    queryFn: () =>
      apiRequest<WhatIWatch>(
        `/api/me/bots/${encodeURIComponent(botId)}/what-i-watch`,
        {},
        whatIWatchSchema,
      ),
    enabled: open,
  });
  const budget = useQuery({
    queryKey: queryKeys.watchBudget(),
    queryFn: () => apiRequest<WatchBudget>('/api/me/watch-budget', {}, watchBudgetSchema),
    enabled: open,
  });

  const stop = useMutation<unknown, Error, string>({
    mutationFn: (watchId) =>
      apiRequest(`/api/me/watches/${encodeURIComponent(watchId)}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.whatIWatch(botId) }),
  });

  return {
    yours: panel.data?.yours ?? [],
    partOfYourJob: panel.data?.partOfYourJob ?? [],
    isLoading: panel.isLoading,
    loadError: panel.error,
    budgetPercentUsed: budget.data?.percentUsed ?? null,
    stop: (watchId: string) => stop.mutate(watchId),
    stopping: stop.isPending ? stop.variables : null,
    stopError: stop.error,
  };
};
